import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BlockIcon from '@mui/icons-material/Block';
import DownloadIcon from '@mui/icons-material/Download';

// Services
import { PotrosacProps, Simulacija, SimulacijaRezultat } from '@/services/plinService';

interface IZatvaranjeVentilaPanelProps {
  simulacije: Simulacija[];
  selected: Simulacija | null;
  rezultat: SimulacijaRezultat | null;
  loading: boolean;
  placingNewLeak: boolean;
  onSelect: (id: number) => void;
  onNewZatvaranje: () => void;
  onNewCurenje: () => void;
  onRename: (sim: Simulacija, naziv: string) => void;
  onDelete: (sim: Simulacija) => void;
  onRemoveValves: (ventilIds: number[]) => void;
  focusedPotrosacId: number | null;
  onPotrosacClick: (id: number) => void;
}

const ROWS_PER_PAGE = 50;

const adresa = (p: PotrosacProps) => [p.ulica, p.kucni_broj].filter(Boolean).join(' ') + (p.stan ? `, stan ${p.stan}` : '');

// semicolon + BOM so Croatian Excel opens it with the right columns and čćžšđ
const exportCsv = (rez: SimulacijaRezultat) => {
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const header = ['ID', 'Kupac ID', 'Naziv', 'Naselje', 'Ulica', 'Kućni broj', 'Stan', 'Aktivan', 'Kategorija'];
  const rows = rez.potrosaci.features.map(({ properties: p }) =>
    [p.id, p.kupac_id, p.naziv, p.naselje, p.ulica, p.kucni_broj, p.stan, p.aktivan ? 'DA' : 'NE', p.kategorija].map(esc).join(';')
  );
  const blob = new Blob(['﻿' + [header.map(esc).join(';'), ...rows].join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${rez.naziv.replace(/[^\p{L}\p{N}_ -]/gu, '')}_potrosaci.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const Stat = ({ label, value, sub }: { label: React.ReactNode; value: string | number; sub?: React.ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 1, flex: 1, minWidth: 90 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" sx={{ lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary">
        {sub}
      </Typography>
    )}
  </Paper>
);

const ZatvaranjeVentilaPanel = (props: IZatvaranjeVentilaPanelProps) => {
  const { simulacije, selected, rezultat, loading, placingNewLeak } = props;
  const { onSelect, onNewZatvaranje, onNewCurenje, onRename, onDelete, onRemoveValves, focusedPotrosacId, onPotrosacClick } = props;
  const { t } = useTranslation();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const potrosaci = useMemo(() => {
    const all = rezultat?.potrosaci.features.map((f) => f.properties) ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((p) =>
      [p.naziv, p.naselje, p.ulica, p.kucni_broj, p.kupac_id].some((v) =>
        String(v ?? '')
          .toLowerCase()
          .includes(q)
      )
    );
  }, [rezultat, search]);

  const hint = placingNewLeak
    ? t('plin:hint.novo_curenje')
    : selected?.tip === 'zatvaranje'
    ? t('plin:hint.zatvaranje')
    : selected?.tip === 'curenje'
    ? t('plin:hint.curenje')
    : t('plin:hint.odaberi');

  const offGraph = rezultat?.tip === 'zatvaranje' ? rezultat.ventili.features.filter((f) => !f.properties.in_graph) : [];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" startIcon={<BlockIcon />} onClick={onNewZatvaranje}>
            {t('plin:buttons.novo_zatvaranje')}
          </Button>
          <Button size="small" variant={placingNewLeak ? 'outlined' : 'contained'} startIcon={<WaterDropIcon />} onClick={onNewCurenje}>
            {placingNewLeak ? t('plin:buttons.odustani') : t('plin:buttons.novo_curenje')}
          </Button>
        </Stack>
        <Alert severity="info" sx={{ mt: 1, py: 0 }}>
          {hint}
        </Alert>
      </Box>

      <List dense sx={{ maxHeight: 200, overflow: 'auto', borderTop: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        {simulacije.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
            {t('plin:empty.simulacije')}
          </Typography>
        )}
        {simulacije.map((sim) => (
          <ListItemButton key={sim.id} selected={sim.id === selected?.id} onClick={() => onSelect(sim.id)}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              {sim.tip === 'curenje' ? <WaterDropIcon fontSize="small" color="primary" /> : <BlockIcon fontSize="small" />}
            </ListItemIcon>
            {editingId === sim.id ? (
              <TextField
                size="small"
                variant="standard"
                autoFocus
                fullWidth
                value={editName}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editName.trim()) {
                    onRename(sim, editName.trim());
                    setEditingId(null);
                  }
                  if (e.key === 'Escape') setEditingId(null);
                }}
              />
            ) : (
              <ListItemText
                primary={sim.naziv}
                secondary={sim.tip === 'zatvaranje' ? t('plin:list.ventila', { count: sim.ventil_ids.length }) : t('plin:tip.curenje')}
              />
            )}
            {sim.is_mine && (
              <>
                {editingId === sim.id ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (editName.trim()) onRename(sim, editName.trim());
                      setEditingId(null);
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Tooltip title={t('plin:buttons.preimenuj')}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingId(sim.id);
                        setEditName(sim.naziv);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={t('plin:buttons.obrisi')}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(sim);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </ListItemButton>
        ))}
      </List>

      <Box sx={{ height: 4, flexShrink: 0 }}>{loading && <LinearProgress />}</Box>

      {rezultat && selected && rezultat.id === selected.id && (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden', flex: 1 }}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Stat
              label={t('plin:stat.potrosaci')}
              value={rezultat.summary.potrosaci.toLocaleString('hr')}
              sub={t('plin:stat.aktivnih', { count: rezultat.summary.potrosaci_aktivni })}
            />
            <Stat
              label={t('plin:stat.plinovodi')}
              value={`${(rezultat.summary.plinovodi_m / 1000).toLocaleString('hr', { maximumFractionDigits: 1 })} km`}
            />
            <Stat
              label={rezultat.tip === 'curenje' ? t('plin:stat.ventili_zatvoriti') : t('plin:stat.ventili_zatvoreni')}
              value={rezultat.summary.ventili}
            />
            {rezultat.tip === 'curenje' && <Stat label={t('plin:stat.mrs')} value={rezultat.summary.mrs} />}
          </Stack>

          {rezultat.tip === 'curenje' && rezultat.summary.mrs > 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              {t('plin:warning.mrs_u_segmentu')}
            </Alert>
          )}
          {offGraph.length > 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              {t('plin:warning.ventil_izvan_mreze', { ids: offGraph.map((f) => f.properties.id).join(', ') })}
            </Alert>
          )}

          {rezultat.tip === 'zatvaranje' && rezultat.ventili.features.length > 0 && (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {rezultat.ventili.features.map((f) => (
                <Chip
                  key={f.properties.id}
                  size="small"
                  label={`${t('plin:ventil')} ${f.properties.id}`}
                  onDelete={() => onRemoveValves([f.properties.id])}
                />
              ))}
            </Stack>
          )}
          {rezultat.tip === 'curenje' && (
            <Typography variant="body2" color="text.secondary">
              {t('plin:legend.curenje')}
            </Typography>
          )}

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder={t('plin:table.trazi') as string}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{ flex: 1 }}
            />
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              disabled={!rezultat.potrosaci.features.length}
              onClick={() => exportCsv(rezultat)}
            >
              CSV
            </Button>
          </Stack>

          <TableContainer sx={{ flex: 1, minHeight: 120 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t('plin:table.naziv')}</TableCell>
                  <TableCell>{t('plin:table.adresa')}</TableCell>
                  <TableCell>{t('plin:table.naselje')}</TableCell>
                  <TableCell>{t('plin:table.aktivan')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {potrosaci.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE).map((p) => (
                  <TableRow
                    key={p.id}
                    hover
                    selected={p.id === focusedPotrosacId}
                    onClick={() => onPotrosacClick(p.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{p.naziv}</TableCell>
                    <TableCell>{adresa(p)}</TableCell>
                    <TableCell>{p.naselje}</TableCell>
                    <TableCell>{p.aktivan ? t('plin:table.da') : t('plin:table.ne')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={potrosaci.length}
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            onPageChange={(_, p) => setPage(p)}
            sx={{ flexShrink: 0 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ZatvaranjeVentilaPanel;
